import {
  CATEGORIES,
  PRIORITY_LABELS,
  centsToInput,
  firstUrl,
  importHint,
  importedCategory,
  isHttpUrl,
  parseMoney,
  validateItemForm,
  type Category,
  type Item,
  type ItemFormError,
  type ItemInput,
  type Board,
  type ImportResult,
  type Outfit,
  type PagePayload,
  type Priority,
} from '@closetly/core';
import { useEffect, useRef, useState } from 'react';
import { shrinkPhoto, storedImageUrl } from '../lib/image';
import { importLink, importPage } from '../lib/queries';
import { PriceWatch } from './PriceWatch';
import { Sheet } from './Sheet';

/** Where a new piece is saved right away. */
export interface SaveTo {
  outfits: string[];
  boards: string[];
}

export interface SharedLink {
  url: string;
  text: string;
  /** Collected by the browser button; used instead of fetching the link. */
  page?: PagePayload;
}

interface Props {
  /** The item being edited, or null for a new one. */
  item: Item | null;
  defaultCategory: Category;
  /** A link shared from another app; it is imported right away. */
  shared?: SharedLink | null;
  /** Outfits and boards a new piece can be saved to right away; null hides the choice. */
  saveTo?: { outfits: Outfit[]; boards: Board[]; outfitIds: string[]; boardIds: string[] } | null;
  onSave: (data: ItemInput, to: SaveTo) => void;
  onDelete: (item: Item) => void;
  onClose: () => void;
}

const PRIORITIES: Priority[] = [1, 2, 3];

function initialValues(item: Item | null, defaultCategory: Category, shared?: SharedLink | null) {
  return {
    name: item?.name ?? '',
    price: item ? centsToInput(item.price_cents) : '',
    category: item?.category ?? defaultCategory,
    shop: item?.shop ?? '',
    size: item?.size ?? '',
    color: item?.color ?? '',
    link: item?.link ?? shared?.url ?? '',
    priority: item?.priority ?? 2,
    note: item?.note ?? '',
    bought: item?.bought ?? false,
    image_source_url: item?.image_source_url ?? '',
    watch: item?.price_watch ?? false,
    target: centsToInput(item?.target_price_cents ?? 0),
  };
}

/** Mount with a `key` per item so the form starts fresh each time it opens. */
export function ItemSheet({
  item,
  defaultCategory,
  shared,
  saveTo = null,
  onSave,
  onDelete,
  onClose,
}: Props) {
  const [v, setV] = useState(() => initialValues(item, defaultCategory, shared));
  const [toOutfits, setToOutfits] = useState<string[]>(saveTo?.outfitIds ?? []);
  const [toBoards, setToBoards] = useState<string[]>(saveTo?.boardIds ?? []);
  const flip = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const [error, setError] = useState<ItemFormError | null>(null);
  const [importing, setImporting] = useState(!!shared);
  const [hint, setHint] = useState('');
  const [categoryTouched, setCategoryTouched] = useState(!!item);
  const [photo, setPhoto] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<HTMLInputElement>(null);

  const set =
    <K extends keyof typeof v>(key: K) =>
    (value: (typeof v)[K]) =>
      setV((prev) => ({ ...prev, [key]: value }));

  const keepsStoredImage = !!item?.image && !removeImage && !photo;
  const photoUrl = useObjectUrl(photo);
  // the shop picture is only previewed here; the server stores its own copy
  const preview = photoUrl || (keepsStoredImage ? storedImageUrl(item!, '800x0') : '') || v.image_source_url;

  const fetchFromLink = (url: string, text = '') => runImport(() => importLink(url, text));

  async function runImport(load: () => Promise<ImportResult>) {
    setImporting(true);
    setHint('');
    try {
      const r = await load();
      const p = r.product;
      const cat = importedCategory(p);
      const takeImage = !photo && !keepsStoredImage && !!p.image_url;
      setV((prev) => ({
        ...prev,
        link: p.url || prev.link,
        name: prev.name || p.name,
        price: prev.price || (p.price_cents ? centsToInput(p.price_cents) : ''),
        shop: prev.shop || p.shop,
        color: prev.color || p.color,
        category: !categoryTouched && cat ? cat : prev.category,
        image_source_url: takeImage ? p.image_url : prev.image_source_url,
      }));
      setHint(importHint(r));
    } catch {
      setHint(
        navigator.onLine
          ? 'Das hat nicht geklappt. Du kannst alles auch selbst eintragen.'
          : 'Ohne Verbindung kann ich den Link nicht abrufen.',
      );
    } finally {
      setImporting(false);
    }
  }

  // a link shared from a shop app is fetched as soon as the sheet opens
  const sharedOnce = useRef(shared);
  useEffect(() => {
    const s = sharedOnce.current;
    if (s) void (s.page ? runImport(() => importPage(s.page!)) : fetchFromLink(s.url, s.text));
    // runs once on open; runImport only touches state through setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function pickPhoto(file: File | undefined) {
    if (!file) return;
    setPhoto(await shrinkPhoto(file));
    setRemoveImage(false);
  }

  function dropImage() {
    setPhoto(null);
    setRemoveImage(true);
    set('image_source_url')('');
  }

  function submit() {
    const err = validateItemForm(v);
    if (err) {
      setError(err);
      ({ name: nameRef, price: priceRef, link: linkRef, target: targetRef })[err.field].current?.focus();
      return;
    }
    const data: ItemInput = {
      name: v.name.trim(),
      price_cents: parseMoney(v.price) ?? 0,
      category: v.category,
      shop: v.shop.trim(),
      size: v.size.trim(),
      color: v.color.trim(),
      link: v.link.trim(),
      note: v.note.trim(),
      priority: v.priority,
      bought: v.bought,
      // an own photo replaces the shop picture for good
      image_source_url: photo ? '' : v.image_source_url,
      // without a link there is nothing to check
      price_watch: v.watch && isHttpUrl(v.link.trim()),
      target_price_cents: parseMoney(v.target) ?? 0,
    };
    if (photo) data.image = photo;
    else if (removeImage) data.image = null;
    onSave(data, { outfits: toOutfits, boards: toBoards });
  }

  const linkReady = isHttpUrl(v.link.trim());

  return (
    <Sheet open onClose={onClose} onSubmit={submit} labelledBy="itemTitle">
      <h2 id="itemTitle">{item ? 'Teil bearbeiten' : 'Neues Teil'}</h2>

      <div className="field">
        <label htmlFor="f-link">Link zum Produkt</label>
        <div className="linkrow">
          <input
            id="f-link"
            ref={linkRef}
            value={v.link}
            onChange={(e) => set('link')(e.target.value)}
            onPaste={(e) => {
              const pasted = firstUrl(e.clipboardData.getData('text'));
              if (pasted && !item && !v.name) {
                e.preventDefault();
                set('link')(pasted);
                void fetchFromLink(pasted);
              }
            }}
            type="url"
            inputMode="url"
            placeholder="Link aus dem Shop einfügen"
            autoComplete="off"
          />
          <button
            type="button"
            className="btn"
            disabled={!linkReady || importing}
            onClick={() => fetchFromLink(v.link.trim())}
          >
            {importing ? 'Hole…' : 'Daten holen'}
          </button>
        </div>
        <p className="hint" role="status" aria-live="polite">
          {importing ? 'Schaue im Shop nach…' : hint}
        </p>
      </div>
      {error?.field === 'link' && (
        <div className="err" role="alert">
          {error.message}
        </div>
      )}

      <div className="photo">
        {preview ? (
          <img src={preview} alt="Bild des Teils" referrerPolicy="no-referrer" />
        ) : (
          <div className="photo-empty" aria-hidden="true">
            Kein Bild
          </div>
        )}
        <div className="photo-actions">
          <label className="btn">
            {preview ? 'Anderes Foto' : 'Foto hinzufügen'}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                void pickPhoto(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          {preview && (
            <button type="button" className="btn" onClick={dropImage}>
              Bild entfernen
            </button>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="f-name">Was ist es?</label>
        <input
          id="f-name"
          ref={nameRef}
          value={v.name}
          onChange={(e) => set('name')(e.target.value)}
          placeholder="z. B. Leinenhemd, oversized"
          autoComplete="off"
          data-autofocus={item || shared ? undefined : true}
          required
        />
      </div>
      <div className="two">
        <div className="field">
          <label htmlFor="f-price">Preis (€)</label>
          <input
            id="f-price"
            ref={priceRef}
            value={v.price}
            onChange={(e) => set('price')(e.target.value)}
            inputMode="decimal"
            placeholder="49,90"
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label htmlFor="f-cat">Kategorie</label>
          <select
            id="f-cat"
            value={v.category}
            onChange={(e) => {
              setCategoryTouched(true);
              set('category')(e.target.value as Category);
            }}
          >
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="err" role="alert">
        {error?.field === 'name' || error?.field === 'price' ? error.message : ''}
      </div>
      <PriceWatch
        item={item}
        watch={v.watch}
        onWatch={set('watch')}
        target={v.target}
        onTarget={set('target')}
        targetRef={targetRef}
        targetError={error?.field === 'target' ? error.message : ''}
        price={v.price}
        link={v.link}
        linkReady={linkReady}
        onChecked={(cents) => set('price')(centsToInput(cents))}
      />
      <div className="field">
        <label htmlFor="f-shop">Marke oder Shop</label>
        <input
          id="f-shop"
          value={v.shop}
          onChange={(e) => set('shop')(e.target.value)}
          placeholder="z. B. COS, Zalando"
          autoComplete="off"
        />
      </div>
      <div className="two">
        <div className="field">
          <label htmlFor="f-size">Größe</label>
          <input
            id="f-size"
            value={v.size}
            onChange={(e) => set('size')(e.target.value)}
            placeholder="M, 38, 42…"
            autoComplete="off"
          />
        </div>
        <div className="field">
          <label htmlFor="f-color">Farbe</label>
          <input
            id="f-color"
            value={v.color}
            onChange={(e) => set('color')(e.target.value)}
            placeholder="Olivgrün"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="field" role="radiogroup" aria-labelledby="f-prio">
        <span className="label" id="f-prio">
          Wie dringend?
        </span>
        <div className="radios">
          {PRIORITIES.map((p) => (
            <label key={p}>
              <input
                type="radio"
                name="prio"
                value={p}
                checked={v.priority === p}
                onChange={() => set('priority')(p)}
              />
              <span>{PRIORITY_LABELS[p]}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="field">
        <label htmlFor="f-note">Notiz</label>
        <textarea
          id="f-note"
          value={v.note}
          onChange={(e) => set('note')(e.target.value)}
          placeholder="Warte auf Sale, passt zur schwarzen Jeans…"
        />
      </div>
      {saveTo && saveTo.outfits.length + saveTo.boards.length > 0 && (
        <div className="field" role="group" aria-labelledby="f-saveto">
          <span className="label" id="f-saveto">
            Merken in
          </span>
          <div className="chips wrap-chips">
            {saveTo.boards.map((b) => (
              <button
                key={b.id}
                type="button"
                className="chip"
                aria-pressed={toBoards.includes(b.id)}
                onClick={() => setToBoards((l) => flip(l, b.id))}
              >
                {b.name}
              </button>
            ))}
            {saveTo.outfits.map((o) => (
              <button
                key={o.id}
                type="button"
                className="chip outfit-chip"
                aria-pressed={toOutfits.includes(o.id)}
                onClick={() => setToOutfits((l) => flip(l, o.id))}
              >
                Outfit: {o.name || 'ohne Namen'}
              </button>
            ))}
          </div>
        </div>
      )}
      <label className="check">
        <input type="checkbox" checked={v.bought} onChange={(e) => set('bought')(e.target.checked)} /> Schon gekauft
      </label>
      <div className="sheet-actions">
        {item && (
          <button type="button" className="btn danger" onClick={() => onDelete(item)}>
            Löschen
          </button>
        )}
        <div className="right">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn ink">
            Speichern
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** A blob: URL for a local file, revoked when the file changes or the sheet closes. */
function useObjectUrl(file: File | null): string {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors an external resource
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return file ? url : '';
}
